import React, { useRef, useEffect, useState } from 'react';
import { TimelineState } from '@xzdarcy/react-timeline-editor';
import { Application, Graphics, FederatedPointerEvent, Sprite } from 'pixi.js';
import TextRenderer from './TextRenderer';
import CharacterRenderer from './CharacterRenderer';
import ImageRenderer from './ImageRenderer.tsx';
import EffectsRenderer from './EffectsRenderer';
import RiggedCharacterRenderer from './RiggedCharacterRenderer';
import AssetRenderer from './AssetRenderer';
import {
  CharacterRef,
  TextProps,
  Effect,
  Layer,
  Position,
  AssetType,
  RiggedCharacterRef,
  TextRef,
  SpriteWithId,
  Comment,
  CommentDrawingRef,
} from '../../types/typing';
import { drawGrid } from './utils/drawGrid';
import { CharacterRig } from '../../types/RiggingTypes';
import './pixicanvas.css';
import DrawingRenderer from './DrawingRenderer.tsx';
import { isBackgroundRecordingApp } from './utils/backgroundRecordingState';

const isPixiAppAlive = (app: Application | null): app is Application =>
  Boolean(app && (app as { renderer?: unknown }).renderer);

interface PixiCanvasProps {
  width: number;
  height: number;
  backgroundColor?: number;
  image?: string | { src: string; startTime?: number } | null;
  animation?: any[];
  selectedTexts?: TextProps[];
  effect?: Effect[] | null | undefined;
  assets?: any[];
  layers?: Layer[] | undefined;
  charactersRef: React.MutableRefObject<Map<string, CharacterRef>>;
  effectsSpriteRef: React.MutableRefObject<SpriteWithId | null>;
  backgroundImageRef: React.MutableRefObject<Sprite | null>;
  textRef: React.MutableRefObject<Map<string, TextRef>>;
  onUpdateCharacter: (
    id: string,
    properties: {
      position?: { x: number; y: number };
      scale?: number;
      pathPoints?: Position[];
    }
  ) => void;
  onUpdateAsset: (
    index: number,
    properties: { position?: { x: number; y: number }; scale?: number }
  ) => void;
  onUpdateText: (id: string, textProps: Partial<TextProps>) => void;
  onAssetSelection: (id: string, assetName: AssetType) => void;
  isRecording: boolean;
  recordingStartTime: number | null;
  selectedMenu: string | null | undefined;
  comments: Comment[];
  isCommentsOpen: boolean;
  handleAddCommentDrawingPoint: (point: Position) => void;
  drawingRef: React.MutableRefObject<Graphics | null>;
  commentContainerRef: React.MutableRefObject<Map<string, CommentDrawingRef>>;
  appRef: React.MutableRefObject<Application | null>;
  setLastSelectedCharacterIndex: (index: string) => void;
  // Rigged character props
  riggedCharactersRef?: React.MutableRefObject<Map<string, RiggedCharacterRef>>;
  riggedCharacters?: Array<{
    id: string;
    rig: CharacterRig;
    position: Position;
    scale: number;
    actionId?: string;
  }>;
  onUpdateRiggedCharacter?: (
    id: string,
    properties: { position?: Position; scale?: number }
  ) => void;
  timelineState?: React.MutableRefObject<TimelineState | null>;
  /** Fired when the canvas background is painted (see ImageRenderer). */
  onBackgroundReady?: () => void;
}
const PixiCanvas: React.FC<PixiCanvasProps> = ({
  width,
  height,
  backgroundColor = 0xffffff,
  image,
  animation,
  selectedTexts,
  effect,
  assets,
  layers,
  charactersRef,
  effectsSpriteRef,
  backgroundImageRef,
  textRef,
  onUpdateCharacter,
  onUpdateAsset,
  onUpdateText,
  onAssetSelection,
  isRecording,
  recordingStartTime,
  selectedMenu,
  isCommentsOpen,
  comments,
  handleAddCommentDrawingPoint,
  drawingRef,
  commentContainerRef,
  appRef,
  setLastSelectedCharacterIndex,
  riggedCharactersRef,
  riggedCharacters,
  onUpdateRiggedCharacter,
  timelineState,
  onBackgroundReady,
}) => {
  const pixiContainer = useRef<HTMLDivElement>(null);
  const [pixiApp, setPixiApp] = useState<Application | null>(null);

  const isDrawing = useRef(false);
  const isCommentsOpenRef = useRef<boolean>(false);
  // Holds the pencil (comment drawing) Graphics so strokes are drawn onto a
  // single reusable layer instead of piling up orphaned layers on the stage.
  const drawingLayerRef = useRef<Graphics | null>(null);

  useEffect(() => {
    isCommentsOpenRef.current = isCommentsOpen;
  }, [isCommentsOpen]);

  useEffect(() => {
    let isMounted = true;
    let pixiAppInstance: Application | null = null;
    let handleClickOutsidePixi: ((event: MouseEvent) => void) | null = null;

    const initPixi = async (): Promise<void> => {
      if (!pixiContainer.current) return;

      try {
        const PIXI = await import('pixi.js');
        if (!isMounted) return;

        const app = new PIXI.Application();
        await app.init({
          width,
          height,
          backgroundColor,
          resolution: window.devicePixelRatio || 1,
          antialias: true,
          autoDensity: true,
        });
        if (!isMounted) {
          app.destroy(true, { children: true });
          return;
        }

        pixiAppInstance = app;
        app.stage.interactive = true;

        pixiContainer.current.appendChild(app.canvas);
        appRef.current = app;
        setPixiApp(app);

        const grid = new PIXI.Graphics();
        drawGrid(grid, width, height);
        app.stage.addChild(grid);

        const startDrawing = () => {
          if (!isCommentsOpenRef.current) return;
          // Reuse the existing drawing layer if it's still alive, otherwise
          // create a fresh one (e.g. after it was cleared on comment save).
          let drawingLayer = drawingLayerRef.current;
          if (!drawingLayer || drawingLayer.destroyed) {
            drawingLayer = new PIXI.Graphics();
            drawingLayerRef.current = drawingLayer;
            app.stage.addChild(drawingLayer);
          }
          drawingRef.current = drawingLayer;
          isDrawing.current = true;
        };

        const draw = (event: FederatedPointerEvent) => {
          if (!isDrawing.current || !drawingRef.current) return;
          const pos = event.data.global;
          const pointPosition: Position = { x: pos.x, y: pos.y };

          handleAddCommentDrawingPoint(pointPosition);

          drawingRef.current.beginFill(0x32cd32);
          drawingRef.current.drawCircle(pos.x, pos.y, 1);
          drawingRef.current.endFill();
        };

        const stopDrawing = () => {
          isDrawing.current = false;
        };

        app.stage.eventMode = 'static';
        app.stage.hitArea = app.screen;
        app.stage.on('pointerdown', startDrawing);
        app.stage.on('pointermove', draw);
        app.stage.on('pointerup', stopDrawing);
        app.stage.on('pointerupoutside', stopDrawing);

        // Click anywhere on the document (outside Pixi Canvas)
        handleClickOutsidePixi = (event: MouseEvent) => {
          if (!pixiContainer.current?.contains(event.target as Node)) {
            // Deselect all characters
            charactersRef.current.forEach((char) => {
              if (char.isSelected) {
                // char.isSelected = false;
                if (char.borderGraphics) char.borderGraphics.visible = false;
              }
            });
          }
        };

        // Listen for clicks outside Pixi
        document.addEventListener('mousedown', handleClickOutsidePixi);
      } catch (error) {
        console.error('Error initializing PixiJS:', error);
        if (pixiContainer.current) {
          pixiContainer.current.classList.add('canvas-error');
        }
      }
    };

    initPixi();

    return () => {
      isMounted = false;
      if (handleClickOutsidePixi) {
        document.removeEventListener('mousedown', handleClickOutsidePixi);
      }
      if (pixiAppInstance) {
        if (isBackgroundRecordingApp(pixiAppInstance)) {
          return;
        }

        if (appRef.current === pixiAppInstance) {
          appRef.current = null;
        }
        setPixiApp((currentApp) =>
          currentApp === pixiAppInstance ? null : currentApp
        );

        if (isPixiAppAlive(pixiAppInstance)) {
          pixiAppInstance.destroy(true, { children: true });
        }
      }
    };
  }, [width, height, backgroundColor]);

  useEffect(() => {
    if (!isPixiAppAlive(pixiApp)) return;

    const canvas = pixiApp.canvas as HTMLCanvasElement | null | undefined;
    if (canvas) {
      if (isCommentsOpen) {
        //canvas.style.cursor = "url('src/assets/icons/cursors/pen-icon.png'), auto";
        canvas.style.cursor = 'pointer';
      } else {
        canvas.style.cursor = 'default';
      }
    }
  }, [isCommentsOpen, pixiApp]);

  const activePixiApp = isPixiAppAlive(pixiApp) ? pixiApp : null;

  return (
    <div className="canvas-wrapper">
      <div
        ref={pixiContainer}
        className="pixi-container"
        style={{ width, height }}
      />
      <ImageRenderer
        app={activePixiApp}
        image={image}
        width={width}
        height={height}
        backgroundImageRef={backgroundImageRef}
        zIndex={1}
        timelineState={timelineState}
        onBackgroundReady={onBackgroundReady}
      />
      <TextRenderer
        app={activePixiApp}
        selectedTexts={selectedTexts}
        width={width}
        height={height}
        layers={layers}
        textRef={textRef}
        onUpdateText={onUpdateText}
        onAssetSelection={onAssetSelection}
        isRecording={isRecording}
        selectedMenu={selectedMenu}
        recordingStartTime={recordingStartTime}
      />
      <CharacterRenderer
        app={activePixiApp}
        charactersRef={charactersRef}
        animation={animation}
        width={width}
        height={height}
        onUpdateCharacter={onUpdateCharacter}
        isRecording={isRecording}
        recordingStartTime={recordingStartTime}
        onAssetSelection={onAssetSelection}
        layers={layers}
        selectedMenu={selectedMenu}
        setLastSelectedCharacterIndex={setLastSelectedCharacterIndex}
      />
      <AssetRenderer
        pixiContainer={pixiContainer}
        assets={assets}
        width={width}
        height={height}
        onUpdateAsset={onUpdateAsset}
      />
      <EffectsRenderer
        app={activePixiApp}
        effectsSpriteRef={effectsSpriteRef}
        effect={effect}
        width={width}
        height={height}
        zIndex={image ? 2 : 1}
        layers={layers}
      />
      <DrawingRenderer
        app={activePixiApp}
        commentContainerRef={commentContainerRef}
        comments={comments}
        isCommentsOpen={isCommentsOpen}
        width={width}
        height={height}
        zIndex={layers && layers.length > 0 ? layers.length - 1 : 0}
        layers={layers}
        timelineState={timelineState}
      />
      {riggedCharactersRef && riggedCharacters && onUpdateRiggedCharacter && (
        <RiggedCharacterRenderer
          app={activePixiApp}
          riggedCharactersRef={riggedCharactersRef}
          riggedCharacters={riggedCharacters}
          width={width}
          height={height}
          layers={layers}
          onUpdateRiggedCharacter={onUpdateRiggedCharacter}
          onAssetSelection={onAssetSelection}
          timelineState={timelineState}
          isRecording={isRecording}
          recordingStartTime={recordingStartTime}
          selectedMenu={selectedMenu}
        />
      )}
    </div>
  );
};

export default PixiCanvas;
